import z from 'zod';
import { request } from '../../utils/request';
import { PersonalKycSchema } from '../../schemas/storeGetStartedSchema';
import uploadFile from '../fileUpload/uploadFile';
import { FilePurpose } from '../../libs/interfaces/filePurpose';

type SaveKycBody = {
  verification_type: 'NIN' | 'BVN' | 'UTILITY_BILL';
  file_id?: number;
  meta_info?: Record<string, any>;
};

const postKycDoc = (body: SaveKycBody, authToken: string) =>
  request({
    method: 'POST',
    url: '/auth/user-kyc/documents',
    headers: { Authorization: `Bearer ${authToken}` },
    formData: body,
    onError: (err) => {
      throw err;
    },
  });

type SaveUserKycReq = {
  body: z.infer<typeof PersonalKycSchema>;
  authToken: string;
  /** Field names the user actually touched in this submit. */
  dirty: Partial<Record<keyof z.infer<typeof PersonalKycSchema>, boolean>>;
};

export const saveUserKycDocuments = async ({
  body,
  authToken,
  dirty,
}: SaveUserKycReq) => {
  const tasks: Array<Promise<unknown>> = [];

  // NIN — file + document_number
  if (dirty.NIN_FILE || dirty.NIN_NUMBER) {
    const file = body.NIN_FILE;
    if (file instanceof File) {
      const upload = await uploadFile({
        file,
        file_purpose: FilePurpose.NIN,
        requires_image: true,
      });
      tasks.push(
        postKycDoc(
          {
            verification_type: 'NIN',
            file_id: upload.file_id,
            meta_info: body.NIN_NUMBER
              ? { document_number: body.NIN_NUMBER }
              : undefined,
          },
          authToken
        )
      );
    }
  }

  // BVN — number only, no file
  if (dirty.BVN_NUMBER && body.BVN_NUMBER) {
    tasks.push(
      postKycDoc(
        {
          verification_type: 'BVN',
          meta_info: {
            document_number: body.BVN_NUMBER,
            bvn_number: body.BVN_NUMBER,
          },
        },
        authToken
      )
    );
  }

  // UTILITY_BILL — file + optional address
  if (dirty.UTILITY_BILL_FILE || dirty.UTILITY_BILL_ADDRESS) {
    const file = body.UTILITY_BILL_FILE;
    if (file instanceof File) {
      const upload = await uploadFile({
        file,
        file_purpose: FilePurpose.UTILITY_BILL,
        requires_image: true,
      });
      tasks.push(
        postKycDoc(
          {
            verification_type: 'UTILITY_BILL',
            file_id: upload.file_id,
            meta_info: body.UTILITY_BILL_ADDRESS
              ? { document_number: body.UTILITY_BILL_ADDRESS }
              : undefined,
          },
          authToken
        )
      );
    }
  }

  return Promise.all(tasks);
};
